import React, { createContext, useContext, useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { authApi } from '../services/api'
import { User, SkillPermission, SalesPermission } from '../types'

interface EvaluatorInfo {
  is_self_evaluator: boolean
  is_manager_evaluator: boolean
  is_dept_head_evaluator: boolean
  is_goal_approver_evaluator: boolean
}

interface EvaluationPeriodStatus {
  hasActivePeriod: boolean
  activePeriodId: string | null
  canSetGoals: boolean
  canEditGoals?: boolean
  canApproveGoals?: boolean
  canSubmitGoalsForApproval?: boolean
  goalApprovalStatus?: string | null
  canSubmitSelfEvaluation: boolean
  canSubmitManagerEvaluation: boolean
  canSubmitDeptEvaluation: boolean
  canSubmitHRReview: boolean
  currentPhase: string | null
  systemStatus: string
}

interface AuthContextType {
  user: User | null
  evaluatorInfo: EvaluatorInfo | null
  periodStatus: EvaluationPeriodStatus | null
  skillPermissions: SkillPermission[]
  salesPermissions: SalesPermission | null
  loading: boolean
  login: (employeeId: string, password: string) => Promise<void>
  logout: () => void
  updateUser: (user: User) => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const queryClient = useQueryClient()
  const [user, setUser] = useState<User | null>(null)
  const [evaluatorInfo, setEvaluatorInfo] = useState<EvaluatorInfo | null>(null)
  const [periodStatus, setPeriodStatus] = useState<EvaluationPeriodStatus | null>(null)
  const [skillPermissions, setSkillPermissions] = useState<SkillPermission[]>([])
  const [salesPermissions, setSalesPermissions] = useState<SalesPermission | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const initializeAuth = async () => {
      setLoading(true)
      const token = localStorage.getItem('auth_token')

      if (token) {
        try {
          const response = await authApi.getCurrentUser()
          const userData: any = response.data.data || response.data
          setUser(userData.user || userData)

          // Set skill and sales permissions from response
          setSkillPermissions(userData.skillPermissions || [])
          setSalesPermissions(userData.salesPermissions || null)

          // Fetch evaluator info
          try {
            const evaluatorResponse = await authApi.getEvaluatorInfo()
            setEvaluatorInfo(evaluatorResponse.data.data)
          } catch (evalError) {
            console.error('Failed to fetch evaluator info:', evalError)
            setEvaluatorInfo(null)
          }

          // Fetch evaluation period status
          try {
            const periodStatusResponse = await authApi.getEvaluationPeriodStatus()
            const responseData = periodStatusResponse.data.data
            // period.period_idを抽出してactivePeriodIdとして設定
            setPeriodStatus({
              ...responseData,
              activePeriodId: responseData.period?.period_id || null
            })
          } catch (periodError) {
            console.error('Failed to fetch period status:', periodError)
            setPeriodStatus(null)
          }
        } catch (error) {
          console.error('Failed to get current user:', error)
          localStorage.removeItem('auth_token')
          setUser(null)
          setEvaluatorInfo(null)
          setPeriodStatus(null)
          setSkillPermissions([])
          setSalesPermissions(null)
        }
      }

      setLoading(false)
    }

    initializeAuth()
  }, [])

  const login = async (employeeId: string, password: string) => {
    // ログイン前に全てのキャッシュをクリア（前のユーザーのデータを削除）
    queryClient.clear()

    const response = await authApi.login(employeeId, password)
    // APIレスポンスは { success, message, token, user, skillPermissions, salesPermissions } の構造
    const { token, user: userData, skillPermissions: userSkillPermissions, salesPermissions: userSalesPermissions } = response.data

    localStorage.setItem('auth_token', token)
    setUser(userData as User)

    // Set skill and sales permissions from login response
    setSkillPermissions(userSkillPermissions || [])
    setSalesPermissions(userSalesPermissions || null)

    // Fetch evaluator info
    try {
      const evaluatorResponse = await authApi.getEvaluatorInfo()
      setEvaluatorInfo(evaluatorResponse.data.data)
    } catch (evalError) {
      console.error('Failed to fetch evaluator info:', evalError)
      setEvaluatorInfo(null)
    }

    // Fetch evaluation period status
    try {
      const periodStatusResponse = await authApi.getEvaluationPeriodStatus()
      const responseData = periodStatusResponse.data.data
      // period.period_idを抽出してactivePeriodIdとして設定
      setPeriodStatus({
        ...responseData,
        activePeriodId: responseData.period?.period_id || null
      })
    } catch (periodError) {
      console.error('Failed to fetch period status:', periodError)
      setPeriodStatus(null)
    }
  }

  const logout = async () => {
    try {
      await authApi.logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      localStorage.removeItem('auth_token')
      setUser(null)
      setEvaluatorInfo(null)
      setPeriodStatus(null)
      setSkillPermissions([])
      setSalesPermissions(null)
      // ログアウト時に全てのキャッシュをクリア
      queryClient.clear()
    }
  }

  const updateUser = (updatedUser: User) => {
    setUser(updatedUser)
  }

  const value = {
    user,
    evaluatorInfo,
    periodStatus,
    skillPermissions,
    salesPermissions,
    loading,
    login,
    logout,
    updateUser,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}